import forgotPasswordImageUrl from "@/assets/forgot-password.svg"
import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import { Label } from "@/components/chadcn/Label"
import HeroImage from "@/components/HeroImage"
import { EmailInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSendForgotPasswordEmailMutation } from "@/data/queries"
import { ROUTES } from "@/routes"
import { forgotPasswordSchema, type ForgotPasswordFormData } from "@/schemas/auth"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useLocation, useNavigate } from "react-router"
import { toast } from "sonner"

type ForgotPasswordPageStateParams = {
  email?: string
}

const ForgotPasswordPage = () => {
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const navigate = useNavigate()
  const locationState = useLocation().state as ForgotPasswordPageStateParams | null
  const {
    mutateAsync: sendForgotPasswordEmail,
    isPending,
    isSuccess,
  } = useSendForgotPasswordEmailMutation()

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    mode: "all",
    defaultValues: { email: locationState?.email ?? "" },
    resolver: zodResolver(forgotPasswordSchema),
  })

  useEffect(() => {
    if (isSuccess && hasSubmitted) {
      toast.success("Email sent successfully! Please check your email.")
    }
  }, [isSuccess, hasSubmitted])

  const onSubmit = async (formData: ForgotPasswordFormData) => {
    try {
      await sendForgotPasswordEmail({ email: formData.email })
      setHasSubmitted(true)
    } catch {
      // Errors surface via the mutation; no toast.
    }
  }

  return (
    <Page showAppbar={false}>
      <div className="absolute left-4 top-4 z-10">
        <BackButton
          customClickHandler={() =>
            navigate(ROUTES.SIGN_IN, {
              replace: true,
              state: { email: getValues("email") },
            })
          }
        />
      </div>
      <HeroImage src={forgotPasswordImageUrl} alt="forgot-password" />
      <h2 className="mb-8 px-6 text-center text-h2 font-bold">
        Please enter the email address associated with your account
      </h2>

      <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-1">
          <Label htmlFor="email">Email</Label>
          <EmailInput
            id="email"
            autoComplete="email"
            disabled={isPending}
            {...register("email")}
            placeholder="email@example.com"
            isError={!!errors.email?.message}
          />
          {errors.email?.message && <p className="text-sm text-error">{errors.email.message}</p>}
        </div>

        <Button type="submit" disabled={isPending} className="flex w-full gap-3 py-2 text-base">
          {isPending && <Spinner />}
          Send Email
        </Button>
      </form>
    </Page>
  )
}

export default ForgotPasswordPage
